// AICleanupCard — per-item "✨ AI Cleanup" (Batch 3 · Feature G + Wave 2).
// Manual trigger (never auto-runs). Renders the cleaned build prompt AND, when
// present, structured field suggestions as "apply" chips. Nothing is written
// until the human clicks Apply (per field) or "Apply all suggestions".

import { useState } from "react";
import DOMPurify from "dompurify";
import { marked } from "marked";
import api from "../../lib/api";
import { Sparkles, RefreshCw, Copy, AlertTriangle, Check } from "lucide-react";

// Human labels for suggestion keys, in display order.
const SUGGESTION_ORDER = ["title", "menu_area", "priority", "function", "effort", "impact", "roi", "phase"];
const SUGGESTION_LABEL = {
  title: "Title", menu_area: "Menu area", priority: "Priority", function: "Function",
  effort: "Effort", impact: "Impact", roi: "ROI", phase: "Phase",
};

export default function AICleanupCard({ item, onUpdated, onApply, areas = [] }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [applied, setApplied] = useState({});   // key -> true once applied
  const cleanup = item.ai_cleanup;
  const html = cleanup?.text ? marked.parse(cleanup.text, { breaks: true }) : "";
  const suggestions = cleanup?.suggestions || {};
  const suggKeys = SUGGESTION_ORDER.filter((k) => suggestions[k] !== undefined && suggestions[k] !== null && suggestions[k] !== "");

  const run = async () => {
    setBusy(true); setError(""); setApplied({});
    try {
      const { data } = await api.post(`/items/${item.id}/ai-cleanup`);
      onUpdated && onUpdated(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to run AI cleanup");
    } finally {
      setBusy(false);
    }
  };

  const copy = () => cleanup?.text && navigator.clipboard.writeText(cleanup.text);

  // Build the PATCH payload for one suggestion key.
  const patchFor = (key) => {
    const v = suggestions[key];
    if (key === "menu_area") {
      const a = areas.find((x) => (x.label || "").toLowerCase() === String(v).toLowerCase());
      return a ? { menu_area: a.label, menu_area_id: a.id } : { menu_area: v };
    }
    if (key === "impact") return { impact: Number(v) };
    if (key === "roi") return { roi: Number(v) };
    return { [key]: v };
  };

  const applyOne = async (key) => {
    if (!onApply) return;
    await onApply(patchFor(key));
    setApplied((s) => ({ ...s, [key]: true }));
  };
  const applyAll = async () => {
    if (!onApply) return;
    const merged = {};
    suggKeys.forEach((k) => Object.assign(merged, patchFor(k)));
    await onApply(merged);
    const all = {}; suggKeys.forEach((k) => (all[k] = true));
    setApplied(all);
  };

  return (
    <div className="card-dark p-6" data-testid="ai-cleanup-card">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-fuchsia-300" />
          <h2 className="font-display text-xl font-bold">AI Cleanup</h2>
          {cleanup?.is_stale && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-widest border border-amber-400/40 bg-amber-500/10 text-amber-200" title="Item content changed since this was generated" data-testid="ai-cleanup-stale">
              <AlertTriangle className="w-3 h-3" /> Stale
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {cleanup?.text && (
            <button type="button" onClick={copy} className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs border border-white/10 hover:bg-white/5 text-slate-300" data-testid="ai-cleanup-copy">
              <Copy className="w-3 h-3" /> Copy
            </button>
          )}
          <button type="button" onClick={run} disabled={busy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border border-fuchsia-400/40 bg-fuchsia-500/15 hover:bg-fuchsia-500/25 text-fuchsia-100 disabled:opacity-40"
            data-testid="ai-cleanup-run">
            {busy ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            {busy ? "Thinking…" : (cleanup?.text ? "Regenerate" : "Run AI cleanup")}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-3 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2" data-testid="ai-cleanup-error">{error}</div>
      )}

      {/* Field suggestions — apply-per-field or apply-all. Nothing writes until clicked. */}
      {suggKeys.length > 0 && (
        <div className="mb-4 p-3 rounded-lg border border-fuchsia-400/20 bg-fuchsia-500/[0.04]" data-testid="ai-suggestions">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-mono uppercase tracking-widest text-fuchsia-200">Suggested fields</span>
            <button type="button" onClick={applyAll} className="text-[11px] font-semibold px-2 py-1 rounded border border-fuchsia-400/40 bg-fuchsia-500/15 hover:bg-fuchsia-500/25 text-fuchsia-100" data-testid="ai-apply-all">
              Apply all
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {suggKeys.map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => applyOne(k)}
                disabled={applied[k]}
                data-testid={`ai-suggest-${k}`}
                className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] border transition-colors ${
                  applied[k]
                    ? "border-emerald-400/40 bg-emerald-500/10 text-emerald-200 cursor-default"
                    : "border-white/15 bg-white/[0.03] text-slate-200 hover:border-fuchsia-400/40 hover:bg-fuchsia-500/10"
                }`}
              >
                <span className="text-slate-400 uppercase tracking-wide text-[9.5px]">{SUGGESTION_LABEL[k]}:</span>
                <span className="font-semibold">{String(suggestions[k])}</span>
                {applied[k] ? <Check className="w-3 h-3" /> : <span className="text-fuchsia-300">apply</span>}
              </button>
            ))}
          </div>
        </div>
      )}

      {!cleanup?.text ? (
        <div className="text-[11px] text-slate-500 py-3 border border-dashed border-white/10 rounded-md text-center">
          Runs Gemini 3 Flash against the current title, description, attachments and full comment thread (voice transcripts included),
          grounded on this system's real menu areas. Rewrites everything into a fixed dev-friendly template and suggests scoring fields.
        </div>
      ) : (
        <>
          <div className="rich-content" data-testid="ai-cleanup-body" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }} />
          <div className="mt-3 text-[10px] font-mono text-slate-500">
            {cleanup.model} · generated {new Date(cleanup.generated_at).toLocaleString()}
            {cleanup.generated_by_name ? ` · by ${cleanup.generated_by_name}` : ""}
          </div>
        </>
      )}
    </div>
  );
}
